import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Store, Globe, Users, CreditCard, Mail, FolderOpen, 
  Package, Search, ChevronRight, ChevronLeft, Check,
  SkipForward, Sparkles, ArrowLeft, PartyPopper, Shield,
  Sun, Moon
} from 'lucide-react';
import { AdminSidebar, AdminSidebarToggle } from '@/components/admin/AdminSidebar';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Définition des 8 étapes
const STEPS = [
  { id: 1, title: "Informations boutique", icon: Store, description: "Nom, logo et slogan" },
  { id: 2, title: "Langues et devises", icon: Globe, description: "Localisation de votre boutique" },
  { id: 3, title: "Connexion agent", icon: Users, description: "Configuration Playwright" },
  { id: 4, title: "Moyens de paiement", icon: CreditCard, description: "Stripe, PayPal, Bit" },
  { id: 5, title: "Configuration email", icon: Mail, description: "Notifications et support" },
  { id: 6, title: "Premières catégories", icon: FolderOpen, description: "Minimum 3 catégories" },
  { id: 7, title: "Premier import", icon: Package, description: "Ajouter un produit" },
  { id: 8, title: "Configuration SEO", icon: Search, description: "Métadonnées et robots.txt" },
];

// Emojis pour catégories
const CATEGORY_EMOJIS = ['👕', '👖', '👟', '🧢', '🎒', '⌚', '🕶️', '💍', '🧥', '👔', '🩳', '🧦'];

export function AdminOnboardingPage() {
  const navigate = useNavigate();
  const { isLight, toggleTheme } = useAdminTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [skippedSteps, setSkippedSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [summary, setSummary] = useState(null);
  
  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Step data states
  const [stepData, setStepData] = useState({
    step1: { store_name: { fr: '', he: '', en: '' }, slogan: { fr: '', he: '', en: '' } },
    step2: { enabled_languages: ['fr', 'he', 'en'], default_language: 'fr', default_currency: 'ILS', currency_switch_enabled: true },
    step3: { agent_name: '', agent_email: '', agent_password: '' },
    step4: { stripe_enabled: true, stripe_api_key: '', paypal_enabled: false, paypal_client_id: '', paypal_secret: '', bit_enabled: false, bit_api_key: '' },
    step5: { resend_api_key: '', sender_email: 'noreply@mutagim.shop', reply_to_email: 'support@mutagim.shop' },
    step6: { categories: [
      { name: { fr: '', he: '', en: '' }, emoji: '👕' },
      { name: { fr: '', he: '', en: '' }, emoji: '👖' },
      { name: { fr: '', he: '', en: '' }, emoji: '👟' }
    ]},
    step7: { product_url: '', publish_directly: false, ai_generation_enabled: true },
    step8: { meta_title: { fr: '', he: '', en: '' }, meta_description: { fr: '', he: '', en: '' }, google_search_console: '', robots_txt_enabled: true }
  });
  
  // Check auth
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    } else {
      setIsLoading(false);
    }
  }, []);
  
  // Fetch onboarding status
  const fetchStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/onboarding/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('admin_token');
        return;
      }
      
      const data = await response.json();
      setCurrentStep(data.current_step || 1);
      setCompletedSteps(data.completed_steps || []);
      setSkippedSteps(data.skipped_steps || []);
      setIsCompleted(data.is_completed || false);
      
      if (data.is_completed && data.summary) {
        setSummary(data.summary);
      }
    } catch (err) {
      console.error('Failed to fetch onboarding status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchStatus();
    }
  }, [isAuthenticated, token, fetchStatus]);
  
  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok || data.role !== 'admin') {
        throw new Error(data.detail || 'Admin access required');
      }
      
      setToken(data.access_token);
      setIsAuthenticated(true);
      localStorage.setItem('admin_token', data.access_token);
      toast.success('Connexion réussie');
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  // Start onboarding
  const startOnboarding = async () => {
    try {
      await fetch(`${API_URL}/api/admin/onboarding/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchStatus();
    } catch (err) {
      toast.error('Erreur au démarrage');
    }
  };
  
  // Save current step
  const saveStep = async () => {
    setIsSaving(true);
    
    try {
      const stepKey = `step${currentStep}`;
      let bodyData = {};
      
      switch (currentStep) {
        case 1:
          bodyData = stepData.step1;
          break;
        case 2:
          bodyData = stepData.step2;
          break;
        case 3:
          bodyData = stepData.step3;
          break;
        case 4:
          bodyData = stepData.step4;
          break;
        case 5:
          bodyData = stepData.step5;
          break;
        case 6:
          bodyData = stepData.step6;
          break;
        case 7:
          bodyData = stepData.step7;
          break;
        case 8:
          bodyData = stepData.step8;
          break;
        default:
          break;
      }
      
      const response = await fetch(`${API_URL}/api/admin/onboarding/step/${currentStep}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur de sauvegarde');
      }
      
      toast.success(`Étape ${currentStep} sauvegardée`);
      setCompletedSteps(prev => [...new Set([...prev, currentStep])]);
      setSkippedSteps(prev => prev.filter(s => s !== currentStep));
      
      if (data.notice) {
        toast.info(data.notice);
      }
      
      if (data.is_final_step) {
        // Compléter l'onboarding
        await completeOnboarding();
      } else if (data.next_step) {
        setCurrentStep(data.next_step);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Skip step
  const skipStep = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/onboarding/skip/${currentStep}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      setSkippedSteps(prev => [...new Set([...prev, currentStep])]);
      setCompletedSteps(prev => prev.filter(s => s !== currentStep));
      
      toast.info(`Étape ${currentStep} ignorée`);
      
      if (data.is_final_step) {
        await completeOnboarding();
      } else if (data.next_step) {
        setCurrentStep(data.next_step);
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };
  
  // Complete onboarding
  const completeOnboarding = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/onboarding/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      setSummary(data.summary);
      setIsCompleted(true);
      toast.success('Onboarding terminé !');
    } catch (err) {
      toast.error('Erreur de finalisation');
    }
  };
  
  // Test email
  const testEmail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/onboarding/step/5/test-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error('Test échoué');
      }
    } catch (err) {
      toast.error('Erreur du test');
    }
  };
  
  // Update step data helper
  const updateStepData = (step, field, value) => {
    setStepData(prev => ({
      ...prev,
      [step]: { ...prev[step], [field]: value }
    }));
  };
  
  // Update nested step data
  const updateNestedStepData = (step, field, lang, value) => {
    setStepData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        [field]: { ...prev[step][field], [lang]: value }
      }
    }));
  };
  
  // Add category
  const addCategory = () => {
    const randomEmoji = CATEGORY_EMOJIS[Math.floor(Math.random() * CATEGORY_EMOJIS.length)];
    setStepData(prev => ({
      ...prev,
      step6: {
        ...prev.step6,
        categories: [...prev.step6.categories, { name: { fr: '', he: '', en: '' }, emoji: randomEmoji }]
      }
    }));
  };
  
  // Update category
  const updateCategory = (index, field, value, lang = null) => {
    setStepData(prev => {
      const newCategories = [...prev.step6.categories];
      if (lang) {
        newCategories[index] = {
          ...newCategories[index],
          name: { ...newCategories[index].name, [lang]: value }
        };
      } else {
        newCategories[index] = { ...newCategories[index], [field]: value };
      }
      return { ...prev, step6: { ...prev.step6, categories: newCategories } };
    });
  };
  
  // Progress percentage
  const progressPercent = ((completedSteps.length + skippedSteps.length) / 8) * 100;
  
  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900/80 border-zinc-800">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-xl text-white">Wizard Onboarding</CardTitle>
            <CardDescription>Connectez-vous pour configurer votre boutique</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-white">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="admin@mutagim.shop"
                  required
                  data-testid="onboarding-email-input"
                />
              </div>
              <div>
                <Label className="text-white">Mot de passe</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="••••••••"
                  required
                  data-testid="onboarding-password-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="onboarding-login-button">
                Se connecter
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  // Completion screen
  if (isCompleted && summary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-zinc-900/80 border-zinc-800" data-testid="onboarding-complete-screen">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <PartyPopper className="w-8 h-8 text-green-400" />
            </div>
            <CardTitle className="text-2xl text-white">Félicitations !</CardTitle>
            <CardDescription className="text-lg">
              Votre boutique Mutagim.shop est configurée
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="text-3xl font-bold text-green-400">{summary.completed_count}</div>
                <div className="text-sm text-white">Étapes complétées</div>
              </div>
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="text-3xl font-bold text-yellow-400">{summary.skipped_count}</div>
                <div className="text-sm text-white">Étapes ignorées</div>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-white">Récapitulatif</h4>
              <div className="space-y-1">
                {STEPS.map(step => {
                  const isComplete = summary.completed_steps?.includes(step.id);
                  const isSkipped = summary.skipped_steps?.includes(step.id);
                  return (
                    <div key={step.id} className="flex items-center justify-between p-2 bg-zinc-800/30 rounded">
                      <div className="flex items-center gap-2">
                        <step.icon className="w-4 h-4 text-gray-200" />
                        <span className="text-sm text-white">{step.title}</span>
                      </div>
                      {isComplete && <Badge className="bg-green-500/20 text-green-400">Complété</Badge>}
                      {isSkipped && <Badge className="bg-yellow-500/20 text-yellow-400">Ignoré</Badge>}
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate('/admin')}
                data-testid="go-to-dashboard-button"
              >
                Aller au Dashboard
              </Button>
              <Button 
                className="flex-1"
                onClick={() => navigate('/')}
              >
                Voir la Boutique
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6" data-testid="step-1-content">
            <div>
              <Label className="text-white mb-2 block">Nom de la boutique</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-gray-200">Français</Label>
                  <Input
                    value={stepData.step1.store_name.fr}
                    onChange={(e) => updateNestedStepData('step1', 'store_name', 'fr', e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="Mutagim.shop"
                    data-testid="store-name-fr"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-200">Hébreu</Label>
                  <Input
                    dir="rtl"
                    value={stepData.step1.store_name.he}
                    onChange={(e) => updateNestedStepData('step1', 'store_name', 'he', e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="מוטאגים.שופ"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-200">English</Label>
                  <Input
                    value={stepData.step1.store_name.en}
                    onChange={(e) => updateNestedStepData('step1', 'store_name', 'en', e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="Mutagim.shop"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Slogan</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  value={stepData.step1.slogan.fr}
                  onChange={(e) => updateNestedStepData('step1', 'slogan', 'fr', e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Streetwear Premium"
                />
                <Input
                  dir="rtl"
                  value={stepData.step1.slogan.he}
                  onChange={(e) => updateNestedStepData('step1', 'slogan', 'he', e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="סטריטוור פרימיום"
                />
                <Input
                  value={stepData.step1.slogan.en}
                  onChange={(e) => updateNestedStepData('step1', 'slogan', 'en', e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Premium Streetwear"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Logo (PNG/SVG)</Label>
                <Input
                  type="file"
                  accept=".png,.svg"
                  className="bg-zinc-800 border-zinc-700"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        updateStepData('step1', 'logo_base64', reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Favicon</Label>
                <Input
                  type="file"
                  accept=".ico,.png,.svg"
                  className="bg-zinc-800 border-zinc-700"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        updateStepData('step1', 'favicon_base64', reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </div>
            </div>
            
            <div className="p-4 bg-zinc-800/30 rounded-lg">
              <Label className="text-white mb-2 block">Couleurs (fixées)</Label>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-[#C0000C]" />
                  <span className="text-sm text-white">mutagim-red</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-[#1A1A1A]" />
                  <span className="text-sm text-white">mutagim-dark</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-white border border-zinc-600" />
                  <span className="text-sm text-white">mutagim-white</span>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6" data-testid="step-2-content">
            <div>
              <Label className="text-white mb-4 block">Langues activées</Label>
              <div className="space-y-3">
                {[
                  { code: 'fr', label: 'Français', flag: '🇫🇷' },
                  { code: 'he', label: 'עברית (Hébreu)', flag: '🇮🇱' },
                  { code: 'en', label: 'English', flag: '🇬🇧' }
                ].map(lang => (
                  <div key={lang.code} className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{lang.flag}</span>
                      <span className="text-white">{lang.label}</span>
                    </div>
                    <Switch
                      checked={stepData.step2.enabled_languages.includes(lang.code)}
                      onCheckedChange={(checked) => {
                        const langs = checked
                          ? [...stepData.step2.enabled_languages, lang.code]
                          : stepData.step2.enabled_languages.filter(l => l !== lang.code);
                        updateStepData('step2', 'enabled_languages', langs);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Langue par défaut</Label>
                <select
                  value={stepData.step2.default_language}
                  onChange={(e) => updateStepData('step2', 'default_language', e.target.value)}
                  className="w-full p-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                >
                  <option value="fr">Français</option>
                  <option value="he">עברית</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <Label className="text-white mb-2 block">Devise par défaut</Label>
                <select
                  value={stepData.step2.default_currency}
                  onChange={(e) => updateStepData('step2', 'default_currency', e.target.value)}
                  className="w-full p-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                >
                  <option value="ILS">₪ ILS (Shekel)</option>
                  <option value="EUR">€ EUR (Euro)</option>
                  <option value="USD">$ USD (Dollar)</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
              <div>
                <div className="text-white">Switch de devise dans le header</div>
                <div className="text-sm text-gray-200">Permet aux visiteurs de changer la devise</div>
              </div>
              <Switch
                checked={stepData.step2.currency_switch_enabled}
                onCheckedChange={(checked) => updateStepData('step2', 'currency_switch_enabled', checked)}
              />
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-6" data-testid="step-3-content">
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-yellow-400 text-sm">
                Connexion Playwright configurée en Phase 5 du développement. 
                Les credentials seront stockés de manière sécurisée.
              </p>
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Nom de l'agent</Label>
              <Input
                value={stepData.step3.agent_name}
                onChange={(e) => updateStepData('step3', 'agent_name', e.target.value)}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Agent Principal"
                data-testid="agent-name-input"
              />
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Email de l'agent</Label>
              <Input
                type="email"
                value={stepData.step3.agent_email}
                onChange={(e) => updateStepData('step3', 'agent_email', e.target.value)}
                className="bg-zinc-800 border-zinc-700"
                placeholder="agent@supplier.com"
                data-testid="agent-email-input"
              />
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Mot de passe de l'agent</Label>
              <Input
                type="password"
                value={stepData.step3.agent_password}
                onChange={(e) => updateStepData('step3', 'agent_password', e.target.value)}
                className="bg-zinc-800 border-zinc-700"
                placeholder="••••••••"
                data-testid="agent-password-input"
              />
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-6" data-testid="step-4-content">
            {/* Stripe */}
            <div className="p-4 bg-zinc-800/30 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#635BFF] rounded flex items-center justify-center text-white font-bold">S</div>
                  <div>
                    <div className="text-white font-medium">Stripe</div>
                    <div className="text-xs text-gray-200">Clé test disponible</div>
                  </div>
                </div>
                <Switch
                  checked={stepData.step4.stripe_enabled}
                  onCheckedChange={(checked) => updateStepData('step4', 'stripe_enabled', checked)}
                />
              </div>
              {stepData.step4.stripe_enabled && (
                <Input
                  value={stepData.step4.stripe_api_key}
                  onChange={(e) => updateStepData('step4', 'stripe_api_key', e.target.value)}
                  className="bg-zinc-700 border-zinc-600"
                  placeholder="sk_test_..."
                />
              )}
            </div>
            
            {/* PayPal */}
            <div className="p-4 bg-zinc-800/30 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#003087] rounded flex items-center justify-center text-white font-bold">P</div>
                  <div>
                    <div className="text-white font-medium">PayPal</div>
                    <div className="text-xs text-gray-200">Client ID + Secret</div>
                  </div>
                </div>
                <Switch
                  checked={stepData.step4.paypal_enabled}
                  onCheckedChange={(checked) => updateStepData('step4', 'paypal_enabled', checked)}
                />
              </div>
              {stepData.step4.paypal_enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={stepData.step4.paypal_client_id}
                    onChange={(e) => updateStepData('step4', 'paypal_client_id', e.target.value)}
                    className="bg-zinc-700 border-zinc-600"
                    placeholder="Client ID"
                  />
                  <Input
                    type="password"
                    value={stepData.step4.paypal_secret}
                    onChange={(e) => updateStepData('step4', 'paypal_secret', e.target.value)}
                    className="bg-zinc-700 border-zinc-600"
                    placeholder="Secret"
                  />
                </div>
              )}
            </div>
            
            {/* Bit */}
            <div className="p-4 bg-zinc-800/30 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#00D4AA] rounded flex items-center justify-center text-black font-bold">B</div>
                  <div>
                    <div className="text-white font-medium">Bit</div>
                    <div className="text-xs text-gray-200">Paiement local Israël</div>
                  </div>
                </div>
                <Switch
                  checked={stepData.step4.bit_enabled}
                  onCheckedChange={(checked) => updateStepData('step4', 'bit_enabled', checked)}
                />
              </div>
              {stepData.step4.bit_enabled && (
                <Input
                  value={stepData.step4.bit_api_key}
                  onChange={(e) => updateStepData('step4', 'bit_api_key', e.target.value)}
                  className="bg-zinc-700 border-zinc-600"
                  placeholder="API Key"
                />
              )}
            </div>
          </div>
        );
      
      case 5:
        return (
          <div className="space-y-6" data-testid="step-5-content">
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-blue-400 text-sm">
                Vous pourrez ajouter votre clé Resend plus tard dans les paramètres.
                En attendant, les emails seront loggés.
              </p>
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Clé API Resend (optionnel)</Label>
              <Input
                value={stepData.step5.resend_api_key}
                onChange={(e) => updateStepData('step5', 'resend_api_key', e.target.value)}
                className="bg-zinc-800 border-zinc-700"
                placeholder="re_..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Adresse expéditeur</Label>
                <Input
                  value={stepData.step5.sender_email}
                  onChange={(e) => updateStepData('step5', 'sender_email', e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Adresse réponse</Label>
                <Input
                  value={stepData.step5.reply_to_email}
                  onChange={(e) => updateStepData('step5', 'reply_to_email', e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            <Button variant="outline" onClick={testEmail} data-testid="test-email-button">
              <Mail className="w-4 h-4 mr-2" />
              Tester l'envoi
            </Button>
          </div>
        );
      
      case 6:
        return (
          <div className="space-y-6" data-testid="step-6-content">
            <p className="text-white text-sm">Créez au minimum 3 catégories pour votre boutique.</p>
            
            <div className="space-y-4">
              {stepData.step6.categories.map((cat, index) => (
                <div key={index} className="p-4 bg-zinc-800/30 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <select
                      value={cat.emoji}
                      onChange={(e) => updateCategory(index, 'emoji', e.target.value)}
                      className="w-16 p-2 bg-zinc-700 border border-zinc-600 rounded text-xl"
                    >
                      {CATEGORY_EMOJIS.map(e => (
                        <option key={e} value={e}>{e}</option>
                      ))}
                    </select>
                    <span className="text-white text-sm">Catégorie {index + 1}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      value={cat.name.fr}
                      onChange={(e) => updateCategory(index, 'name', e.target.value, 'fr')}
                      className="bg-zinc-700 border-zinc-600"
                      placeholder="Nom FR"
                      data-testid={`category-${index}-fr`}
                    />
                    <Input
                      dir="rtl"
                      value={cat.name.he}
                      onChange={(e) => updateCategory(index, 'name', e.target.value, 'he')}
                      className="bg-zinc-700 border-zinc-600"
                      placeholder="שם HE"
                    />
                    <Input
                      value={cat.name.en}
                      onChange={(e) => updateCategory(index, 'name', e.target.value, 'en')}
                      className="bg-zinc-700 border-zinc-600"
                      placeholder="Name EN"
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <Button variant="outline" onClick={addCategory}>
              + Ajouter une catégorie
            </Button>
          </div>
        );
      
      case 7:
        return (
          <div className="space-y-6" data-testid="step-7-content">
            <div className="p-4 bg-zinc-800/30 rounded-lg">
              <p className="text-white text-sm mb-4">
                L'import de produit sera traité automatiquement en Phase 2 du développement 
                (automatisation Playwright). Vous pouvez entrer une URL maintenant pour la file d'attente.
              </p>
            </div>
            
            <div>
              <Label className="text-white mb-2 block">URL du produit à importer</Label>
              <Input
                value={stepData.step7.product_url}
                onChange={(e) => updateStepData('step7', 'product_url', e.target.value)}
                className="bg-zinc-800 border-zinc-700"
                placeholder="https://supplier.com/product/..."
                data-testid="product-url-input"
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                <div>
                  <div className="text-white">Publier directement</div>
                  <div className="text-xs text-gray-200">Sinon, enregistrer en brouillon</div>
                </div>
                <Switch
                  checked={stepData.step7.publish_directly}
                  onCheckedChange={(checked) => updateStepData('step7', 'publish_directly', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <div>
                    <div className="text-white">Génération IA</div>
                    <div className="text-xs text-gray-200">Nom + description automatiques</div>
                  </div>
                </div>
                <Switch
                  checked={stepData.step7.ai_generation_enabled}
                  onCheckedChange={(checked) => updateStepData('step7', 'ai_generation_enabled', checked)}
                />
              </div>
            </div>
          </div>
        );
      
      case 8:
        return (
          <div className="space-y-6" data-testid="step-8-content">
            <div>
              <Label className="text-white mb-2 block">Meta Title</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  value={stepData.step8.meta_title.fr}
                  onChange={(e) => updateNestedStepData('step8', 'meta_title', 'fr', e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Mutagim.shop - Streetwear Premium"
                  data-testid="meta-title-fr"
                />
                <Input
                  dir="rtl"
                  value={stepData.step8.meta_title.he}
                  onChange={(e) => updateNestedStepData('step8', 'meta_title', 'he', e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="מוטאגים - סטריטוור פרימיום"
                />
                <Input
                  value={stepData.step8.meta_title.en}
                  onChange={(e) => updateNestedStepData('step8', 'meta_title', 'en', e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Mutagim.shop - Premium Streetwear"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Meta Description</Label>
              <div className="space-y-3">
                <textarea
                  value={stepData.step8.meta_description.fr}
                  onChange={(e) => updateNestedStepData('step8', 'meta_description', 'fr', e.target.value)}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                  rows={2}
                  placeholder="Description FR..."
                />
                <textarea
                  dir="rtl"
                  value={stepData.step8.meta_description.he}
                  onChange={(e) => updateNestedStepData('step8', 'meta_description', 'he', e.target.value)}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                  rows={2}
                  placeholder="תיאור HE..."
                />
                <textarea
                  value={stepData.step8.meta_description.en}
                  onChange={(e) => updateNestedStepData('step8', 'meta_description', 'en', e.target.value)}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                  rows={2}
                  placeholder="Description EN..."
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Google Search Console (optionnel)</Label>
              <Input
                value={stepData.step8.google_search_console}
                onChange={(e) => updateStepData('step8', 'google_search_console', e.target.value)}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Code de vérification ou URL"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
              <div>
                <div className="text-white">robots.txt</div>
                <div className="text-xs text-gray-200">Autoriser l'indexation par les moteurs</div>
              </div>
              <Switch
                checked={stepData.step8.robots_txt_enabled}
                onCheckedChange={(checked) => updateStepData('step8', 'robots_txt_enabled', checked)}
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  const currentStepInfo = STEPS.find(s => s.id === currentStep);
  
  return (
    <div className={cn(
      "min-h-screen transition-colors duration-200",
      isLight ? "bg-gray-100 admin-light-theme" : "bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
    )}>
      {/* Sidebar */}
      <AdminSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Header */}
        <header className={cn(
          "border-b backdrop-blur-sm sticky top-0 z-40",
          isLight ? "border-gray-200 bg-white/90" : "border-zinc-800 bg-zinc-900/50"
        )}>
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <AdminSidebarToggle onClick={() => setSidebarOpen(true)} />
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/admin')}
                className={isLight ? "text-gray-600 hover:text-gray-900" : ""}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className={cn("text-xl font-bold", isLight ? "text-gray-900" : "text-white")}>Configuration de la boutique</h1>
                <p className={cn("text-sm", isLight ? "text-gray-600" : "text-white")}>Étape {currentStep} sur 8</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={isLight ? "text-gray-700" : "text-white"} data-testid="progress-indicator">
                {completedSteps.length + skippedSteps.length}/8 complétées
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={toggleTheme}
                className={cn(
                  "gap-2 transition-colors",
                  isLight 
                    ? "border-gray-300 bg-white hover:bg-gray-100 text-gray-700" 
                    : "border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                )}
              >
                {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span className="hidden sm:inline">{isLight ? 'Sombre' : 'Clair'}</span>
              </Button>
            </div>
          </div>
        </header>
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress bar */}
          <div className="mb-8">
            <Progress value={progressPercent} className="h-2" />
            <div className="flex justify-between mt-4 overflow-x-auto pb-2">
              {STEPS.map(step => {
                const isComplete = completedSteps.includes(step.id);
                const isSkipped = skippedSteps.includes(step.id);
                const isCurrent = currentStep === step.id;
                
                return (
                  <button
                    key={step.id}
                    onClick={() => setCurrentStep(step.id)}
                    className={`flex flex-col items-center min-w-[80px] p-2 rounded-lg transition-colors ${
                      isCurrent ? 'bg-primary/20' : 'hover:bg-zinc-800/50'
                    }`}
                    data-testid={`step-nav-${step.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                      isComplete ? 'bg-green-500/20 text-green-400' :
                      isSkipped ? 'bg-yellow-500/20 text-yellow-400' :
                      isCurrent ? 'bg-primary/20 text-primary' :
                      'bg-zinc-800 text-gray-200'
                    }`}>
                      {isComplete ? <Check className="w-5 h-5" /> : 
                       isSkipped ? <SkipForward className="w-4 h-4" /> :
                       <step.icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs ${isCurrent ? 'text-white' : 'text-gray-200'}`}>
                      {step.id}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Step content */}
          <Card className="bg-zinc-900/80 border-zinc-800" data-testid="step-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                {currentStepInfo && (
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <currentStepInfo.icon className="w-5 h-5 text-primary" />
                  </div>
                )}
                <div>
                  <CardTitle className="text-xl text-white">
                    {currentStepInfo?.title}
                  </CardTitle>
                  <CardDescription>
                    {currentStepInfo?.description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {renderStepContent()}
            </CardContent>
          </Card>
          
          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-6">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                disabled={currentStep === 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
              <Button
                variant="ghost"
                onClick={skipStep}
                className="text-white"
                data-testid="skip-step-button"
              >
                <SkipForward className="w-4 h-4 mr-1" />
                Ignorer
              </Button>
            </div>
            
            <Button
              onClick={saveStep}
              disabled={isSaving}
              data-testid="save-step-button"
            >
              {isSaving ? (
                <>Sauvegarde...</>
              ) : currentStep === 8 ? (
                <>
                  Terminer
                  <Check className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Sauvegarder et continuer
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
      </div>
    </div>
  );
}

export default AdminOnboardingPage;
